import React from "react";
import { Card, Alert } from 'react-bootstrap';
import { connect } from "react-redux";
import { ValidatableForm } from "../../components/ValidatableForm";
import { QUOTE_STEPS, COMPONENTS, QUOTE_QUESTIONS } from "../../AppConstants";
import { validationUtils } from "../../validation/Validation";
import { IQuoteStepState, IQuoteStepComponentProps } from "../../components/interfaces";
import Utilities from "../../classes/Utilities";
import ButtonContinue from "../../components/ButtonContinue";
import { Button } from 'react-bootstrap';
import { IQuoteApiClient, ILOBApiClient, IPaths, ICoverage } from "../../classes/Domain";
import QuoteActions from "../../state/appActions";
import { IVehicle, ICoverageConfig, IDriver, IContactInfo, IDiscountOption } from "../../classes/Domain";
import VehicleSelectByStep from "../../components/VehicleSelectByStep";
import QuoteStepBase from "../../components/QuoteStepBase";
import { QuickQuoteStep } from "../QuickQuote/QuickQuoteStepLayout";
import { IAppState } from "../../state/interfaces";
import { getDefaultLiabilityCoverages } from "../../config/StateQuoteConfig";
import SwiperCore, { Pagination } from 'swiper';

interface IAppStateUpdate {
    quoteUid: string,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    coverageConfig: ICoverageConfig,
    contactInfo: IContactInfo,
    discounts: IDiscountOption,
    isNonOwners: boolean,
    quickStep: string,
    stateCode: string
}

interface IState extends IQuoteStepState {
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle | null>,
    coverageConfig: ICoverageConfig,
    activeVehicleIndex: number,
    activeDriverIndex: number,
    canAddNewVehicle: boolean,
    canAddNewDriver: boolean,
    continueClick: boolean,
    contactInfo: IContactInfo,
    discounts: IDiscountOption,
    isNonOwners: boolean,
}

export interface IProps extends IQuoteStepComponentProps {
    stateCode: string,
    zipCode: string,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    rateError: string | null,
    PATHS: IPaths,
    getQuickVehicleCoverages: (stateCode?: string) => Array<ICoverage>;
}

class QuickVehicle extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);
        this.onVehicleAdd = this.onVehicleAdd.bind(this);
        this.onVehicleChange = this.onVehicleChange.bind(this);
        this.onVehicleDelete = this.onVehicleDelete.bind(this);

        SwiperCore.use([Pagination]);

        this.state = {
            drivers: [{ currentLicenseStatus: QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE } as IDriver],
            vehicles: [null],
            isNonOwners: false,
            coverageConfig: {
                liabilityCoverages: getDefaultLiabilityCoverages(this.props.stateCode),
                addonCoverages: [],
                vehicleCoverages: [{
                    vehicleId: 0,
                    coverages: this.props.getQuickVehicleCoverages(this.props.stateCode)
                }]
            } as ICoverageConfig,
            activeVehicleIndex: 0,
            activeDriverIndex: 0,
            canAddNewVehicle: false,
            canAddNewDriver: false,
            continueClick: false,
            contactInfo: { phone: '', email: '' } as IContactInfo,
            discounts: {
                installments: true,
                paperless: true
            } as IDiscountOption,
            updatingStore: false,
            errors: null,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();
    }

    subscribeToValidation() {
        validationUtils.subscribeToValidationFailure(QUOTE_STEPS.QUICK_VEHICLE,
            validationData => {
                this.setState({ ...this.state, updatingStore: false, continueClick: false, errors: validationData });
            });
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            nextProps.meta.propsSource = "";
            const vehicles = nextProps.vehicles;
            const drivers = nextProps.drivers;
            const coverageConfig = nextProps.coverageConfig;
            const contactInfo = nextProps.contactInfo;
            const discounts = nextProps.discounts;
            const isNonOwners = nextProps.isNonOwners;
            const initialSlide = nextProps.initialSlide;


            if (vehicles || drivers) {
                let canAddNewVehicle = nextProps.canAddNewVehicle;
                if (!canAddNewVehicle && vehicles.length > 0 && vehicles.every(x => x != null)) {
                    canAddNewVehicle = true;
                }

                let canAddNewDriver = nextProps.canAddNewDriver;
                if (!canAddNewDriver && drivers.length > 0 && drivers.every(x => x.age > 0 && x.maritalStatus && x.gender && x.currentLicenseStatus)) {
                    canAddNewDriver = true;
                }

                const nextState = {
                    ...prevState,
                    vehicles: vehicles,
                    canAddNewVehicle: canAddNewVehicle ?? prevState.canAddNewVehicle,
                    drivers: drivers,
                    canAddNewDriver: canAddNewDriver ?? prevState.canAddNewDriver,
                    coverageConfig: coverageConfig ?? prevState.coverageConfig,
                    contactInfo: contactInfo ?? prevState.contactInfo,
                    discounts: discounts ?? prevState.discounts,
                    isNonOwners: isNonOwners ?? prevState.isNonOwners,
                    initialSlide: initialSlide
                };

                return nextState;
            }
        }

        return prevState;
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(QUOTE_STEPS.QUICK_VEHICLE);
    }

    onVehicleAdd() {
        let _vehicles = this.state.vehicles;
        _vehicles.push(null);

        let _vehicleCoverages = this.state.coverageConfig.vehicleCoverages;
        _vehicleCoverages.push({ vehicleId: _vehicles.length - 1, vehicleDescription: '', coverages: this.props.getQuickVehicleCoverages(this.props.stateCode) });

        this.setState({
            ...this.state, vehicles: _vehicles, activeVehicleIndex: _vehicles.length - 1, canAddNewVehicle: false,
            coverageConfig: { ...this.state.coverageConfig, vehicleCoverages: _vehicleCoverages },
            errors: null
        });
    }

    onVehicleDelete = (index: number) => {
        let _vehicles = this.state.vehicles;
        _vehicles.splice(index, 1);

        let _vehicleCoverages = this.state.coverageConfig.vehicleCoverages;
        _vehicleCoverages.splice(index, 1);

        let _canAddNew = false;

        for (let v of _vehicles) {
            if (v && v.cylinders && v.cylinders > 0) {
                _canAddNew = true;
            }
            else {
                _canAddNew = false;
            }

            if (!_canAddNew) {
                break;
            }
        }

        this.setState({
            ...this.state, vehicles: _vehicles, canAddNewVehicle: _canAddNew,
            coverageConfig: { ...this.state.coverageConfig, vehicleCoverages: _vehicleCoverages }
        });
    }

    onVehicleChange(vehicle: IVehicle, vehicleIndex: number) {
        const _vehicles = [...(this.state.vehicles || [])];
        const _vehicleCoverages = [...(this.state.coverageConfig.vehicleCoverages || [])];

        const _vehicle = _vehicles[vehicleIndex];
        let _vehicleId = vehicleIndex;

        if (_vehicle && _vehicle.id > 0) {
            vehicle.id = _vehicle.id;
            _vehicleId = _vehicle.id;
        }

        let matched = false;
        for (let i = 0; i < _vehicleCoverages.length; i++) {
            if (_vehicleCoverages[i].vehicleId === _vehicleId || _vehicleCoverages[i].vehicleId === vehicleIndex) {
                _vehicleCoverages[i] = {
                    ..._vehicleCoverages[i],
                    vehicleId: _vehicleId,
                    coverages: vehicle.coverages ?? []
                };
                matched = true;
                break;
            }
        }
        if (!matched) {
            if (vehicleIndex < _vehicleCoverages.length) {
                _vehicleCoverages[vehicleIndex] = {
                    ..._vehicleCoverages[vehicleIndex],
                    vehicleId: _vehicleId,
                    coverages: vehicle.coverages ?? []
                };
            } else {
                _vehicleCoverages.push({
                    vehicleId: _vehicleId,
                    vehicleDescription: '',
                    coverages: vehicle.coverages ?? this.props.getQuickVehicleCoverages(this.props.stateCode)
                });
            }
        }

        _vehicles[vehicleIndex] = vehicle;

        this.setState({
            vehicles: _vehicles,
            coverageConfig: { ...this.state.coverageConfig, vehicleCoverages: _vehicleCoverages },
            activeVehicleIndex: vehicleIndex,
            canAddNewVehicle: vehicle?.cylinders > 0
        });
    }

    next(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        event.preventDefault();

        this.setState({ updatingStore: true }, () => {
            const updatedVehicles = [...this.state.vehicles];
            const updatedDrivers = [...this.state.drivers];
            const coverageConfig = this.state.coverageConfig;
            const discounts = this.state.discounts;
            const contactInfo = this.state.contactInfo;

            this.props.updateAppState({
                quoteUid: this.props.quoteUid,
                vehicles: updatedVehicles,
                drivers: updatedDrivers,
                coverageConfig: coverageConfig,
                discounts: discounts,
                contactInfo: contactInfo,
                isNonOwners: false,
                quickStep: QUOTE_STEPS.QUICK_VEHICLE,
                stateCode: this.props.stateCode
            } as IAppStateUpdate)
                .then(() => this.props.history.push(this.props.PATHS.QUICK_DRIVER),
                    () => {
                        this.setState({ ...this.state, updatingStore: false });
                    }
                );
        });
    }

    render() {
        return (
            <QuickQuoteStep id={this.props.id} stateCode={this.props.stateCode} >
                {
                    this.props.rateError
                        ?
                        <div>
                            <Alert variant={"warning"}>
                                <h5 className="text-center">
                                    <strong className="text-uppercase">Sorry we can't continue</strong>
                                    <br />
                                    {this.props.rateError}
                                </h5>
                            </Alert>
                        </div>
                        :
                        null
                }
                {
                    <div>
                        <Card className="quick-quote-data">
                            <Card.Body>
                                <Card className="vehicles-card">
                                    <Card.Body>
                                        <Card.Title>
                                            {"VEHICLE INFO"}
                                        </Card.Title>
                                        <ValidatableForm hasErrors={null != this.state.errors}>
                                            {
                                                this.state.vehicles.map((vehicle, vehicleIndex) => (
                                                    <React.Fragment key={`vehicle-wrapper-${vehicleIndex}`}>
                                                        {vehicleIndex > 0 && (
                                                            <hr className="my-4" style={{ borderColor: '#dee2e6', borderWidth: '2px', borderStyle: 'dashed' }} />
                                                        )}
                                                        <VehicleSelectByStep
                                                            updatingStore={this.state.updatingStore}
                                                            quoteApiClient={this.props.quoteApiClient}
                                                            quoteId={this.props.quoteId}
                                                            quoteUid={this.props.quoteUid}
                                                            stateCode={this.props.stateCode}
                                                            zipCode={this.props.zipCode}
                                                            vehicle={vehicle}
                                                            vehicleIndex={vehicleIndex}
                                                            isActive={this.state.activeVehicleIndex === vehicleIndex}
                                                            onVehicleChange={this.onVehicleChange}
                                                            onVehicleDelete={this.onVehicleDelete}
                                                            getQuickVehicleCoverages={this.props.getQuickVehicleCoverages}
                                                            errors={this.state.errors}
                                                        />
                                                    </React.Fragment>
                                                ))
                                            }
                                            <div className="btnContainer">
                                                <Button
                                                    className="btnAddVehicle"
                                                    onClick={() => { this.onVehicleAdd() }}
                                                    disabled={this.state.updatingStore || !this.state.canAddNewVehicle || this.state.vehicles?.length === 4}> <i className="fas fa-plus"></i> Add vehicle
                                                </Button>
                                            </div>
                                        </ValidatableForm>
                                    </Card.Body>
                                </Card>
                                <div className="break-2" />
                            </Card.Body>
                        </Card>

                        <div className="btnContainer">
                            <ButtonContinue
                                label="Continue"
                                disabled={this.state.updatingStore}
                                showLoading={this.state.updatingStore}
                                onClick={this.next} />
                        </div>
                    </div>
                }
            </QuickQuoteStep>
        );
    }
}

let mapProps = (state: IAppState, ownProps: IProps) => {
    var contactInfo = state.contactInfo;
    if (contactInfo) {
        contactInfo.email = null;
    }

    return {
        quoteUid: state.quoteUid,
        stateCode: state.stateCode,
        zipCode: state.zipCode,
        vehicles: state.vehicles.length > 0 ? state.vehicles : [null],
        coverageConfig: state.coverageConfig,
        drivers: state.drivers.length > 0 ? state.drivers : [{ currentLicenseStatus: QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE } as IDriver],
        contactInfo: state.contactInfo,
        discounts: state.discountOption,
        isNonOwners: state.isNonOwners,
        rateError: state.rateError,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE }
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.QUICK_VEHICLE)),
        updateAppState: (update: IAppStateUpdate) => dispatch(quoteActions.updateQuickData(update.quoteUid, update.vehicles, update.drivers, update.coverageConfig, update.discounts, update.contactInfo, update.isNonOwners, update.quickStep, update.stateCode)),
        getQuickVehicleCoverages: (stateCode?: string) => (quoteActions.GetQuickVehicleCoverages(stateCode))
    };
}

export default connect(mapProps, mapDispatch)(QuickVehicle);
